import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Brain, TreeStructure, ChartBar, Gauge, GitBranch, Lightning } from "@phosphor-icons/react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const capabilities = [
  {
    icon: Brain,
    title: "AI Systems",
    description: "Designing and integrating LLM workflows, meeting intelligence pipelines, and AI-powered operational tools that transform raw data into actionable business signals.",
  },
  {
    icon: TreeStructure,
    title: "Scalable Architecture",
    description: "Building modular microservice architectures with async messaging, event-driven systems, and incremental migration strategies that don't disrupt production users.",
  },
  {
    icon: ChartBar,
    title: "Product Analytics",
    description: "Creating behavioral analytics systems, adoption tracking, and operational intelligence dashboards that drive product decisions with real user signals.",
  },
  {
    icon: Gauge,
    title: "Performance Engineering",
    description: "Optimizing page load times, API response latency, and data pipeline throughput through caching strategies, rendering optimization, and infrastructure tuning.",
  },
  {
    icon: GitBranch,
    title: "Operational Workflows",
    description: "Designing end-to-end product workflows that reduce friction, improve team adoption, and turn manual processes into scalable automated systems.",
  },
  {
    icon: Lightning,
    title: "Real-Time Infrastructure",
    description: "Building real-time data ingestion, processing pipelines, and live analytics systems using Elasticsearch, message queues, and streaming architectures.",
  },
];

export const Capabilities = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="capabilities" ref={ref} data-testid="capabilities-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={{ staggerChildren: 0.1 }}
          className="mb-16"
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-4"
          >
            Systems & Capabilities
          </motion.span>
          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="capabilities-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EDEDED] tracking-tight"
          >
            Engineering depth across the stack.
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((cap, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#121212] border border-white/[0.08] rounded-xl p-6 hover:border-white/[0.15] transition-all duration-300 group"
              data-testid={`capability-card-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-white/[0.05] border border-white/[0.08] flex items-center justify-center mb-5 group-hover:bg-white/[0.08] transition-colors">
                <cap.icon size={20} weight="regular" className="text-[#A1A1AA] group-hover:text-[#EDEDED] transition-colors" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#EDEDED] mb-2 tracking-tight">
                {cap.title}
              </h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                {cap.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
